import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { useTranslations } from '@/hooks/useTranslations';
import { formatBytes } from '@/lib/adoc/format';
import type { AdocEntrySummary } from '@/types/adoc';
import { Download, Eye, FileText, Files } from 'lucide-react';
import { route } from 'ziggy-js';

interface AdocEntryListProps {
    fileId: string;
    entries: AdocEntrySummary[];
    onPreview: (entry: AdocEntrySummary) => void;
}

export function AdocEntryList({ fileId, entries, onPreview }: AdocEntryListProps) {
    const { t } = useTranslations();

    if (entries.length === 0) {
        return (
            <EmptyState
                icon={<Files />}
                title={t('adoc.show.documents.title', 'Documents in this container')}
                description={t('adoc.show.documents.empty', 'This container holds no documents.')}
            />
        );
    }

    return (
        <div className="bg-card divide-y rounded-xl border">
            {entries.map((entry) => (
                <div key={entry.index} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap">
                    <div className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-lg [&>svg]:size-4">
                        <FileText />
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate font-medium">{entry.name}</span>
                            {entry.is_main ? <Badge variant="secondary">{t('adoc.show.documents.main', 'Main document')}</Badge> : null}
                        </div>
                        <div className="text-muted-foreground text-xs">
                            {formatBytes(entry.size)} · {entry.media_type}
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                        {entry.is_pdf ? (
                            <Button variant="outline" size="sm" onClick={() => onPreview(entry)}>
                                <Eye />
                                {t('adoc.show.documents.view', 'View')}
                            </Button>
                        ) : (
                            <span className="text-muted-foreground hidden text-xs sm:inline">
                                {t('adoc.show.documents.not_previewable', 'Only PDF documents can be viewed here — download this file to open it.')}
                            </span>
                        )}
                        <Button asChild variant="outline" size="sm">
                            <a href={route('adoc.entries.download', { adocFile: fileId, entry: entry.index })}>
                                <Download />
                                {t('adoc.show.documents.download', 'Download')}
                            </a>
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    );
}
